// Browse behavior tracking — scroll depth, time on page, filters, engagement
import { useCallback, useEffect, useRef } from "react";
import { trackEvent } from "@/lib/analytics";

// ── Scroll Depth ────────────────────────────────────────────────────────────

const SCROLL_DEBOUNCE_MS = 100;
type Milestone = 25 | 50 | 75 | 100;

export function useScrollDepth(): void {
  const firedRef = useRef(new Set<Milestone>());
  const startRef = useRef(performance.now());

  useEffect(() => {
    let rafId: number | null = null;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const check = () => {
      const scrollY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      const percent = Math.min(100, Math.round((scrollY / docHeight) * 100));
      const milestones: readonly Milestone[] = [25, 50, 75, 100];

      for (const m of milestones) {
        if (percent >= m && !firedRef.current.has(m)) {
          firedRef.current.add(m);
          trackEvent("scroll_depth", {
            depth_percent: m,
            page_url: window.location.pathname,
            time_to_depth_ms: Math.round(performance.now() - startRef.current),
          });
        }
      }
    };

    const onScroll = () => {
      if (timeout !== null) clearTimeout(timeout);
      timeout = setTimeout(() => {
        rafId = requestAnimationFrame(check);
      }, SCROLL_DEBOUNCE_MS);
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (timeout !== null) clearTimeout(timeout);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);
}

// ── Time on Page ────────────────────────────────────────────────────────────

export function useTimeOnPage(pageName: string): void {
  const startRef = useRef(performance.now());
  const activeRef = useRef(0);
  const pausedAtRef = useRef<number | null>(null);
  const interactionsRef = useRef(0);
  const maxScrollRef = useRef(0);

  useEffect(() => {
    const onClick = () => { interactionsRef.current += 1; };
    const onScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) {
        const pct = Math.round((window.scrollY / docHeight) * 100);
        if (pct > maxScrollRef.current) maxScrollRef.current = pct;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        pausedAtRef.current = performance.now();
      } else if (pausedAtRef.current !== null) {
        activeRef.current += performance.now() - pausedAtRef.current;
        pausedAtRef.current = null;
      }
    };

    document.addEventListener("click", onClick);
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("click", onClick);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);

      const total = performance.now() - startRef.current;
      const paused = pausedAtRef.current !== null
        ? performance.now() - pausedAtRef.current + activeRef.current
        : activeRef.current;
      const duration = Math.round(total - paused);

      trackEvent("time_on_page", {
        page_name: pageName,
        duration_ms: duration,
        scroll_depth_max: maxScrollRef.current,
        interactions_count: interactionsRef.current,
        is_bounce: duration < 10000,
      });
    };
  }, [pageName]);
}

// ── Filter Tracking ─────────────────────────────────────────────────────────

export function useFilterTracking() {
  const trackFilterApplied = useCallback(
    (filterName: string, filterValue: string | string[], resultsCount?: number) => {
      trackEvent("filter_applied", {
        filter_name: filterName,
        filter_value: filterValue,
        active_filters_count: 1,
        results_count_after: resultsCount ?? 0,
      });
    },
    [],
  );

  const trackFilterRemoved = useCallback((filterName: string) => {
    trackEvent("filter_removed", { filter_name: filterName, active_filters_count: 0 });
  }, []);

  const trackFilterCleared = useCallback(() => {
    trackEvent("filters_cleared", {});
  }, []);

  const trackSortChanged = useCallback(
    (sortField: string, sortDirection: "asc" | "desc") => {
      trackEvent("sort_changed", {
        sort_field: sortField,
        sort_direction: sortDirection,
      });
    },
    [],
  );

  return { trackFilterApplied, trackFilterRemoved, trackFilterCleared, trackSortChanged };
}

// ── Engagement Score ────────────────────────────────────────────────────────

export function useEngagementScore() {
  const scrollRef = useRef(0);
  const timeRef = useRef(performance.now());
  const clicksRef = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      if (docH > 0) {
        const pct = Math.round((window.scrollY / docH) * 100);
        if (pct > scrollRef.current) scrollRef.current = pct;
      }
    };
    const onClick = () => { clicksRef.current += 1; };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("click", onClick);

    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", onClick);
    };
  }, []);

  const getScore = useCallback((): number => {
    const scrollScore = Math.min(scrollRef.current, 100) * 0.3;
    const elapsed = (performance.now() - timeRef.current) / 1000;
    const timeScore = Math.min(elapsed / 120, 1) * 30; // max at 2 min
    const clickScore = Math.min(clicksRef.current / 10, 1) * 20;
    const contentScore = (scrollRef.current > 50 && elapsed > 30) ? 20 : 0;
    return Math.round(scrollScore + timeScore + clickScore + contentScore);
  }, []);

  return { getScore };
}
